import type { FeedMatchMode, FeedPick } from 'definitions';
import type { FeedDraft } from '@/state/useWebsiteFeeds';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SELECT_TRIGGER = 'border-line bg-field text-foreground h-9 rounded-lg text-body';
const NO_FIELD = '__none__';

export const PICK_OPTIONS: { value: FeedPick; label: string }[] = [
  { value: 'first', label: 'The first item, on every page' },
  { value: 'match', label: 'The item that matches the visitor' },
  { value: 'current', label: 'The item running now, by its dates' },
  { value: 'random', label: 'A random item, the same for a whole visit' },
];

const MODE_OPTIONS: { value: FeedMatchMode; label: string }[] = [
  { value: 'equals', label: 'is the same as' },
  { value: 'contains', label: 'contains' },
  { value: 'path', label: 'is the same address as' },
];

/**
 * The page values a feed is most often matched on, by name. Anything else a token can read
 * is "Another value", typed as its path; a part of the address and an address parameter take
 * a number or a name.
 */
const SOURCES: { value: string; label: string }[] = [
  { value: 'page.path', label: 'The page address' },
  { value: 'segment', label: 'A part of the address' },
  { value: 'page.lastSegment', label: 'The last part of the address' },
  { value: 'query', label: 'An address parameter' },
  { value: 'utm.campaign', label: 'The campaign (utm_campaign)' },
  { value: 'utm.source', label: 'The traffic source (utm_source)' },
  { value: 'visitor.languageCode', label: "The visitor's language" },
  { value: 'page.language', label: "The page's language" },
  { value: 'visitor.status', label: 'New or returning visitor' },
  { value: 'product.sku', label: 'The product SKU' },
  { value: 'product.name', label: 'The product name' },
  { value: 'custom', label: 'Another value' },
];

type SourceChoice = { option: string; extra: string };

/** A match source path as the form shows it: which option, and its number, name or path. */
export const describeSource = (path: string): SourceChoice => {
  const segment = path.match(/^page\.segment\.(\d+)$/);
  if (segment) return { option: 'segment', extra: segment[1] ?? '1' };
  if (path.startsWith('query.')) return { option: 'query', extra: path.slice('query.'.length) };
  if (SOURCES.some((source) => source.value === path && !['segment', 'query', 'custom'].includes(path))) {
    return { option: path, extra: '' };
  }
  return { option: 'custom', extra: path };
};

const sourcePath = ({ option, extra }: SourceChoice) => {
  if (option === 'segment') return `page.segment.${extra || '1'}`;
  if (option === 'query') return extra ? `query.${extra}` : '';
  if (option === 'custom') return extra;
  return option;
};

/** A feed's pick in a few words, for its row in the list. */
export const describePick = (draft: Pick<FeedDraft, 'pick' | 'matchSource' | 'matchField'>) => {
  if (draft.pick === 'first') return 'first item';
  if (draft.pick === 'random') return 'random item per visit';
  if (draft.pick === 'current') return 'item running now';
  return `${draft.matchField} matched on ${draft.matchSource}`;
};

type FieldSelectProps = {
  label: string;
  value: string;
  fields: string[];
  placeholder: string;
  optional?: boolean;
  onChange: (value: string) => void;
};

/** A feed field: picked from the tested feed's fields, or typed before a test. */
const FieldSelect = ({ label, value, fields, placeholder, optional = false, onChange }: FieldSelectProps) =>
  fields.length ? (
    <Select value={value || NO_FIELD} onValueChange={(next) => onChange(next === NO_FIELD ? '' : next)}>
      <SelectTrigger aria-label={label} className={SELECT_TRIGGER}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {optional && <SelectItem value={NO_FIELD}>None</SelectItem>}
        {!optional && !value && <SelectItem value={NO_FIELD}>{placeholder}</SelectItem>}
        {fields.map((field) => (
          <SelectItem key={field} value={field}>
            {field}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <Input
      aria-label={label}
      placeholder={placeholder}
      value={value}
      onChange={(ev) => onChange(ev.target.value)}
      className="h-9 font-mono"
    />
  );

type FeedPickFieldsProps = {
  draft: FeedDraft;
  fields: string[];
  set: (patch: Partial<FeedDraft>) => void;
};

/** Which item a visitor gets, and the rule for it: a match, or the date fields. */
export const FeedPickFields = ({ draft, fields, set }: FeedPickFieldsProps) => {
  const source = describeSource(draft.matchSource);
  const setSource = (next: Partial<SourceChoice>) => set({ matchSource: sourcePath({ ...source, ...next }) });

  return (
    <div className="space-y-2">
      <Select value={draft.pick} onValueChange={(value) => set({ pick: value as FeedPick })}>
        <SelectTrigger aria-label="Which item visitors get" className={SELECT_TRIGGER}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PICK_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {draft.pick === 'match' && (
        <div className="border-line space-y-2 rounded-lg border p-2">
          <div className="grid grid-cols-[1fr_auto] items-center gap-2">
            <Select
              value={source.option}
              onValueChange={(option) => setSource({ option, extra: option === 'segment' ? '1' : '' })}
            >
              <SelectTrigger aria-label="Compare this value of the page" className={SELECT_TRIGGER}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {source.option === 'segment' && (
              <Input
                aria-label="Which part, counting from 1"
                type="number"
                min={1}
                max={8}
                value={source.extra}
                onChange={(ev) => setSource({ extra: ev.target.value })}
                className="h-9 w-20"
              />
            )}
          </div>
          {(source.option === 'query' || source.option === 'custom') && (
            <Input
              aria-label={source.option === 'query' ? 'Parameter name' : 'Data path'}
              placeholder={source.option === 'query' ? 'plan' : 'next.config.content.market'}
              value={source.extra}
              onChange={(ev) => setSource({ extra: ev.target.value.trim() })}
              className="h-9 font-mono"
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Select value={draft.matchMode} onValueChange={(value) => set({ matchMode: value as FeedMatchMode })}>
              <SelectTrigger aria-label="How they are compared" className={SELECT_TRIGGER}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldSelect
              label="The feed field"
              placeholder="Feed field"
              value={draft.matchField}
              fields={fields}
              onChange={(matchField) => set({ matchField })}
            />
          </div>
          <p className="supporting m-0">
            Reads <span className="font-mono">{draft.matchSource || '…'}</span> on the visitor's page. Any value the
            editor's data picker lists can be used.
          </p>
        </div>
      )}

      {draft.pick === 'current' && (
        <div className="grid grid-cols-2 gap-2">
          <FieldSelect
            label="Start date field"
            placeholder="Start date field"
            optional
            value={draft.startField}
            fields={fields}
            onChange={(startField) => set({ startField })}
          />
          <FieldSelect
            label="End date field"
            placeholder="End date field"
            optional
            value={draft.endField}
            fields={fields}
            onChange={(endField) => set({ endField })}
          />
        </div>
      )}
    </div>
  );
};
